import React from 'react';

export default function JobPropertyNotes({ job }) {
  // Extract the note fields from the job's property
  const { housekeepingNotes, stagingNotes, propertyManagerNotes } = job?.property || {};
  
  // Check if any notes exist
  const hasAnyNotes = housekeepingNotes || stagingNotes || propertyManagerNotes;
  
  // If no notes exist, don't render anything
  if (!hasAnyNotes) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg mb-3 lg:mb-6">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 flex items-center">
          <svg className="h-6 w-6 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Property Notes
        </h3>
        
        <div className="space-y-4">
          {/* Housekeeping Notes */}
          {housekeepingNotes && (
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                    <g>
                      <path d="m94.918 64.605c-6.0234-8.0586-15.008-18.184-27.66-20.75 0.53516-1.375 0.25391-2.9961-0.85547-4.1094-1.5039-1.5039-3.9375-1.5039-5.4375 0l-2.7578 2.7578-35.277-35.273c-4.3242-4.3242-11.367-4.3281-15.695 0s-4.3281 11.371 0 15.695l35.273 35.273-2.7578 2.7578c-1.5039 1.5039-1.5039 3.9375 0 5.4375 0.75 0.75 1.7344 1.125 2.7188 1.125 0.46875 0 0.93359-0.097656 1.3789-0.26953 2.5781 12.621 12.707 21.637 20.758 27.656 0.96484 0.72266 2.1289 1.0977 3.3047 1.0977 2.8125 0 5.2383-2.1445 5.5195-5.0039l0.49219-4.9531 5.6406-0.55469c3.125-0.3125 5.6211-2.8086 5.9336-5.9375l0.55859-5.6367 4.9492-0.49219c1.9883-0.19531 3.7227-1.4531 4.5312-3.2812 0.8125-1.8203 0.57031-3.9453-0.61719-5.5391zm-82.246-51.934c1.3242-1.3242 3.4883-1.3281 4.8203 0l35.277 35.273-4.8242 4.8203-35.273-35.277c-1.332-1.3281-1.332-3.4883 0-4.8164zm71.766 53.684c-3.1328 0.30469-5.6328 2.7969-5.9453 5.9375l-0.55859 5.6367-5.6406 0.55469c-3.125 0.3125-5.6211 2.8086-5.9336 5.9375l-0.19531 1.957c-10.66-8.6484-15.441-16.723-15.262-25.695l9.7656-9.7656c9.1289-0.19141 17.066 4.5781 25.723 15.238z"/>
                      <path d="m83.973 38.137c-1.6836 1.8672-1.293 4.1719 0 5.4609 1.5156 1.5156 4.0273 1.4648 5.4219 0 1.582-1.5898 1.4883-3.9727 0-5.4609-1.4258-1.4766-3.9453-1.4766-5.4219 0z"/>
                      <path d="m29.699 73.367c1.2656-1.2656 1.707-3.5547 0-5.4648-1.668-1.625-4.0547-1.3633-5.4219 0-1.6641 1.8633-1.3086 4.1484 0 5.4648 1.6211 1.6211 4.0391 1.3867 5.4219 0z"/>
                      <path d="m45.164 90.441c1.5469-1.4727 1.5273-3.8906 0-5.4219-1.3867-1.4219-3.9609-1.4219-5.4219 0-1.4141 1.4922-1.5703 3.8516 0 5.4219 1.6836 1.6953 4.1992 1.3555 5.4219 0z"/>
                      <path d="m66.895 22.75c-2.4844 2.7578-0.50781 6.5781 2.7305 6.5781 2.6641 0 4.6211-2.7422 3.5391-5.3086-0.19141-0.5-0.46094-0.88672-0.84375-1.2695-1.4258-1.4219-4.043-1.4219-5.4258 0z"/>
                    </g>
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">
                    Housekeeping Notes
                  </h4>
                  <p className="text-sm text-blue-700 whitespace-pre-line">
                    {housekeepingNotes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Staging Notes */}
          {stagingNotes && (
            <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-purple-800 mb-1">
                    Staging Notes
                  </h4>
                  <p className="text-sm text-purple-700 whitespace-pre-line">
                    {stagingNotes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Property Manager Notes */}
          {propertyManagerNotes && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-green-800 mb-1">
                    Property Manager Notes
                  </h4>
                  <p className="text-sm text-green-700 whitespace-pre-line">
                    {propertyManagerNotes}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
